import React, { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import { BrowserRouter as Router, useLocation, useNavigate } from 'react-router-dom';
import { FORM_PATH } from './consts/consts';
import ScrollToTop from './utils/ScrollToTop';
import AppRoutes from './components/AppRoutes';
import { checkLockStatus, fetchFormData } from './api/AuthorizationApi';
import { isRTL, Languages, useLanguage } from './LanguageContext';
import { getTranslation } from './consts/translations';
import {
  extractPathSegments,
  getStoredUuidPathSegment,
  getUuidFromPathname,
} from './utils/urlUtils';
import {
  appendLangForLanguage,
  languageFromCode,
  resolveInitialLanguage,
} from './utils/languageMapping';
import { FormMetadata } from './types';
import { base64DecodeToJson } from './utils/bas64Utils';
import PathErrorPage from './components/PathErrorPage';
import { initLogger, shutdownLogger } from './Logger';

const AppContent: React.FC = () => {
  const [showVerification, setShowVerification] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [inputId, setInputId] = useState('');
  const [inputPhoneOrEmail, setInputPhoneOrEmail] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<FormMetadata | null>(null);
  const [loadingForm, setLoadingForm] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [pathError, setPathError] = useState(false);

  const { language, changeLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const hasNavigated = useRef(false);
  const formSessionLoadedRef = useRef(false);
  const loadedFormUuidRef = useRef<string | null>(null);

  const logout = useCallback(() => {
    shutdownLogger();
    setShowModal(false);
    setInputId('');
    setInputPhoneOrEmail('');
    setShowVerification(false);
    setSessionId('');
    setFormData(null);
    formSessionLoadedRef.current = false;
    hasNavigated.current = false;
    loadedFormUuidRef.current = null;

    const uuid = getStoredUuidPathSegment();
    navigate(appendLangForLanguage(`/login/${uuid}`, language));
  }, [language, navigate]);

  useEffect(() => {
    switch (language) {
      case Languages.ENGLISH:
        document.documentElement.lang = 'en';
        break;
      case Languages.ARABIC:
        document.documentElement.lang = 'ar';
        break;
      case Languages.RUSSIAN:
        document.documentElement.lang = 'ru';
        break;
      case Languages.HEBREW:
      default:
        document.documentElement.lang = 'he';
        break;
    }
    document.documentElement.dir = isRTL(language) ? 'rtl' : 'ltr';
  }, [language]);

  useEffect(() => {
    if (formSessionLoadedRef.current) {
      return;
    }
    if (/^\/preview\/[^/]+$/.test(location.pathname)) {
      return;
    }
    const uuid = getUuidFromPathname(location.pathname) || getStoredUuidPathSegment();
    changeLanguage(resolveInitialLanguage(location.search, uuid), uuid);
  }, [changeLanguage, location.pathname, location.search]);

  useEffect(() => {
    const initializeSession = async () => {
      if (/^\/preview\/[^/]+$/.test(location.pathname)) {
        setLoadingPage(false);
        setLoadingForm(false);
        return;
      }

      try {
        extractPathSegments(location.pathname);
        setPathError(false);
      } catch {
        setPathError(true);
        return;
      }

      const uuid = getStoredUuidPathSegment();

      if (formData !== null && loadedFormUuidRef.current === uuid) {
        formSessionLoadedRef.current = true;
        setLoadingForm(false);
        setLoadingPage(false);
        return;
      }

      if (
        formData !== null &&
        loadedFormUuidRef.current === null &&
        getUuidFromPathname(location.pathname) === uuid
      ) {
        loadedFormUuidRef.current = uuid;
        formSessionLoadedRef.current = true;
        setLoadingForm(false);
        setLoadingPage(false);
        return;
      }

      if (loadedFormUuidRef.current !== null && loadedFormUuidRef.current !== uuid) {
        formSessionLoadedRef.current = false;
        hasNavigated.current = false;
        loadedFormUuidRef.current = null;
        if (formData !== null) {
          setFormData(null);
        }
        return;
      }

      if (formSessionLoadedRef.current && loadedFormUuidRef.current === uuid) {
        setLoadingForm(false);
        setLoadingPage(false);
        return;
      }

      const sessionTranslation = getTranslation(resolveInitialLanguage(location.search, uuid));

      try {
        setLoadingForm(true);
        setLoadingPage(true);

        const data = await fetchFormData(sessionTranslation, logout, uuid);
        const decodedFormData = base64DecodeToJson(data.form_data);
        const resolvedLang = languageFromCode(data.language);
        changeLanguage(resolvedLang, uuid);
        setFormData(decodedFormData);
        formSessionLoadedRef.current = true;
        loadedFormUuidRef.current = uuid;
        initLogger();

        if (!hasNavigated.current) {
          hasNavigated.current = true;
          navigate(appendLangForLanguage(`${FORM_PATH}/${uuid}`, resolvedLang), { replace: true });
        }
      } catch {
        if (!formSessionLoadedRef.current) {
          logout();
          const locked = await checkLockStatus(sessionTranslation);
          if (locked) {
            setShowModal(true);
          }
        }
      }

      setLoadingForm(false);
      setLoadingPage(false);
    };

    initializeSession();
    // changeLanguage intentionally omitted: post-auth language comes from Dynamo/cache;
    // adding it re-runs initializeSession (same class of bug as PSIFAS-12295 deeplink).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, location.pathname, location.search, logout, navigate]);

  const appDirection: React.CSSProperties = {
    direction: isRTL(language) ? 'rtl' : 'ltr',
  };

  if (pathError) {
    return <PathErrorPage />;
  }

  return (
    <div className='app-container' style={appDirection}>
      {loadingPage ? (
        <div className='app-loading-overlay'>
          <div className='app-spinner'></div>
        </div>
      ) : (
        <>
          <ScrollToTop />
          <AppRoutes
            showVerification={showVerification}
            setShowVerification={setShowVerification}
            inputId={inputId}
            inputPhoneOrEmail={inputPhoneOrEmail}
            setInputId={setInputId}
            setInputPhoneOrEmail={setInputPhoneOrEmail}
            sessionId={sessionId}
            setSessionId={setSessionId}
            logout={logout}
            setShowModal={setShowModal}
            showModal={showModal}
            formData={formData}
            setFormData={setFormData}
            loadingForm={loadingForm}
            verificationError={verificationError}
            setVerificationError={setVerificationError}
            setPathError={setPathError}
          />
        </>
      )}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
